import React, { FC, useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import OTPInput from "react-otp-input";
import { KTSVG } from "../../../_metronic/helpers";
import { approveLog, getPendingApprovalInfo, getLogsPending, getLeadLogs, approveLogAsApprover, rejectLog, resendOtp, cancelLog } from "../../../api/Approval";

import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import Swal from "sweetalert2";
import { Modal } from "bootstrap";
import { useTimer } from "../../../hooks/Timer";
import { formatCountdown } from "../../../functions/general";
import { useDataTableStore } from "../../../stores/DataTableStore";
import { getInvoiceGeneratedByLatest } from "../../../api/InvoicesCRUD";
import { InvoiceAttachments, InvoiceRenderContext } from "./InvoiceAttachments";
import { InvoiceMultiPageSetting } from "../../../interfaces/Invoices";
import { useSettingsStore } from "../../../stores/SettingsStore";
import useSWR, { mutate } from "swr";
import { fetchWithToken } from "../../../api/General";

interface OTPModalProps {
    leadId: string;
    leadProgressId: string;
    leadTemplateId: string;
    token: string;
    isOpen: boolean;
    toggleModal: () => void;
    currentUser: UserData | null;
}

interface ApprovalData {
    id: string;
    userName: string[] | string;
    hierarchyLevel: number;
    userId: string[] | string;
    userPhoneNumber: string[] | string;
    approvedAt: string | null;
}

interface UserData {
    id: string;
    name: string;
    username: string;
    phone: string;
}

const OTPModal: FC<OTPModalProps> = ({ leadId, leadProgressId, leadTemplateId, token, isOpen, toggleModal, currentUser }) => {
    const API_URL = process.env.REACT_APP_API_URL
    const { settings } = useSettingsStore()
    const [leadLogId, setLeadLogId] = useState<string>("");
    const [pendingId, setPendingId] = useState<string>("");
    const [approvals, setApprovals] = useState<ApprovalData[]>([]);
    const [currentApproverIndex, setCurrentApproverIndex] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [processingApproval, setProcessingApproval] = useState<boolean>(false);
    const [isCurrentUserApprover, setIsCurrentUserApprover] = useState<boolean>(false);
    const [resendingOtp, setResendingOtp] = useState<boolean>(false);
    const [otp, setOtp] = useState<string>("");
    const [autoSubmitting, setAutoSubmitting] = useState<boolean>(false);
    const { countDown, resetCountDown } = useTimer(60, isOpen);
    const modalRef = useRef<HTMLDivElement | null>(null);
    const closeModalRef = useRef<HTMLButtonElement>(null);
    const intl = useIntl();
    const modalInstanceRef = useRef<Modal | null>(null);
    const { tablesData, setTablesData } = useDataTableStore()
    const navigate = useNavigate();
    const { templatePipelineId } = useParams();
    const [showInvoiceModal, setShowInvoiceModal] = useState<boolean>(false)
    const [invoiceContext, setInvoiceContext] = useState<InvoiceRenderContext>('preview')
    const [selectInvoice, setSelectInvoice] = useState<InvoiceMultiPageSetting>()

    const { data: invoiceSettings = [], error: invoiceSettingsError, isLoading: invoiceSettingsLoading } = useSWR(
        [`${API_URL}invoices/settings/?leadTemplateId=${leadTemplateId}`, token],
        fetchWithToken
    );

    const handleCloseModal = async (e: any) => {
        e.stopPropagation();
        e.preventDefault();

        const result = await Swal.fire({
            title: intl.formatMessage({ id: "FORM.VALIDATION.CANCEL" }),
            text: intl.formatMessage({ id: "FORM.VALIDATION.DESC_CANCEL" }),
            icon: "warning",
            showCancelButton: true,
            buttonsStyling: false,
            confirmButtonText: `${intl.formatMessage({ id: "FORM.CONFIRMATION.YES" })}, ${intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}`,
            cancelButtonText: intl.formatMessage({ id: "FORM.CONFIRMATION.NO" }),
            customClass: {
                confirmButton: "btn btn-danger",
                cancelButton: "btn btn-active-light me-3",
            },
            reverseButtons: true,
            heightAuto: false,
        });

        if (result.isConfirmed) {
            toggleModal();
        }
    };

    const closeModal = () => {
        if (modalInstanceRef.current) {
            modalInstanceRef.current.hide();
        }
        toggleModal();
    };

    useEffect(() => {
        if (!currentUser) {
            Swal.fire({
                icon: "warning",
                title: intl.formatMessage({ id: "FORM.LABEL.USER_MISSING" }),
                text: intl.formatMessage({ id: "FORM.LABEL.LOGIN_AGAIN" }),
            });
            return;
        }
    }, [currentUser]);

    const validationSchema = Yup.object().shape({
        otp: Yup.string()
            .required("OTP Code is required")
            .min(4, "OTP Code must be at least 4 characters")
            .max(4, "OTP Code cannot exceed 4 characters"),
    });

    const currentApprover = React.useMemo(() => {
        if (approvals.length === 0) return null;
        return approvals[currentApproverIndex] || approvals[approvals.length - 1];
    }, [approvals, currentApproverIndex]);

    useEffect(() => {
        if (isOpen && !isCurrentUserApprover) {
            setOtp("");
            formik.setFieldValue("otp", "");
        }
    }, [currentApproverIndex, isOpen, isCurrentUserApprover]);

    useEffect(() => {
        if (approvals.length > 0 && currentUser) {
            const unapprovedIndex = approvals.findIndex(approver => approver.approvedAt === null);

            const newIndex = unapprovedIndex >= 0 ? unapprovedIndex : approvals.length - 1;
            setCurrentApproverIndex(newIndex);

            const approver = approvals[newIndex];

            let isUserApprover = false;
            if (approver) {
                if (Array.isArray(approver.userId)) {
                    isUserApprover = approver.userId.includes(currentUser.id) && approver.approvedAt === null;
                } else {
                    isUserApprover = approver.userId === currentUser.id && approver.approvedAt === null;
                }
            }

            setIsCurrentUserApprover(isUserApprover);
        }
    }, [approvals, currentUser]);

    const formik = useFormik({
        initialValues: {
            otp: "",
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            if (processingApproval) return;

            const { otp } = values;

            if (!isCurrentUserApprover && !otp) {
                Swal.fire({
                    icon: "warning",
                    title: intl.formatMessage({ id: "FORM.LABEL.OTP_MISSING" }),
                    text: intl.formatMessage({ id: "FORM.LABEL.PLS_ENTER_OTP" }),
                });
                return;
            }

            if (!currentApprover) {
                Swal.fire({
                    icon: "error",
                    title: intl.formatMessage({ id: "FORM.LABEL.MISSING_INFO" }),
                    text: intl.formatMessage({ id: "FORM.LABEL.MISSING_INFO_DESC" }),
                });
                return;
            }

            try {
                setProcessingApproval(true);

                if (isCurrentUserApprover) {
                    return;
                }

                const response = await approveLog(leadLogId, pendingId, currentApprover.id, otp, token);

                if (response.data.success) {
                    if (currentApproverIndex < approvals.length - 1) {
                        setCurrentApproverIndex(currentApproverIndex + 1);
                        formik.resetForm();
                        setOtp("");
                        resetCountDown();

                        Swal.fire({
                            icon: "success",
                            title: intl.formatMessage(
                                { id: "FORM.LABEL.APPROVAL_SUBMITTED" },
                                { hierarki: currentApprover.hierarchyLevel }
                            ),
                            text: intl.formatMessage(
                                { id: "FORM.LABEL.APPROVAL_SUBMITTED_DESC" },
                                {
                                    approverName: Array.isArray(currentApprover.userName)
                                        ? currentApprover.userName.join(intl.formatMessage({ id: "FORM.LABEL.OR" }))
                                        : currentApprover.userName
                                }
                            ) as string,
                        });
                    } else {
                        const isFeatureInvoice = (settings.feature_invoice && invoiceSettings && !invoiceSettingsLoading && invoiceSettings.length > 0 && invoiceSettings.some((inv: InvoiceMultiPageSetting) => { return inv.progress?.id === leadProgressId }))
                        if (isFeatureInvoice) {
                            try {
                                const getInvoiceSetting = await getInvoiceGeneratedByLatest(token, leadProgressId, leadId);
                                const masterLogInvoiceUrl = `${API_URL}invoices/generated/list?leadId=${leadProgressId}&progressId=${leadId}`
                                mutate(masterLogInvoiceUrl, undefined, { revalidate: true })
                                if (getInvoiceSetting.data.success) {
                                    setShowInvoiceModal(true)
                                    setInvoiceContext('preview')
                                    setSelectInvoice(getInvoiceSetting.data.data);
                                }
                            } catch (error: any) {
                                Swal.fire({
                                    icon: 'error',
                                    title: error.response.data.message,
                                    confirmButtonText: 'Ok',
                                    heightAuto: false
                                });
                            }
                        }

                        if (tablesData.logs) {
                            const updatedLogs = tablesData.logs.map(log => {
                                if (log.id === leadLogId) {
                                    return { ...log, lead_log_status: 'APPROVED' };
                                }
                                return log;
                            });

                            setTablesData('logs', updatedLogs);
                        }

                        if (tablesData.pipelines) {
                            setTablesData('pipelines', tablesData.pipelines.map(item => {
                                if (item.id === leadId) {
                                    return {
                                        ...item,
                                        has_pending_log: false,
                                    };
                                } else {
                                    return item;
                                }
                            }));
                        }

                        Swal.fire({
                            icon: "success",
                            title: intl.formatMessage({ id: "FORM.LABEL.APPROVALS_COMPLETED" }),
                            text: intl.formatMessage({ id: "FORM.LABEL.APPROVALS_COMPLETED_DESC" }),
                        }).then(() => {
                            if (!isFeatureInvoice) {
                                closeModal();
                                navigate(`/pipelines/${templatePipelineId}`);
                            }
                        });
                    }
                } else {
                    setOtp("");
                    formik.resetForm();
                    Swal.fire({
                        icon: "error",
                        title: intl.formatMessage({ id: "FORM.LABEL.OTP_FAILED" }),
                        text: intl.formatMessage(
                            { id: "FORM.LABEL.OTP_FAILED_DESC" },
                            {
                                approverName: Array.isArray(currentApprover?.userName)
                                    ? currentApprover.userName.join(", ")
                                    : currentApprover?.userName || "",
                            }
                        ),
                    });
                }
            } catch (error) {
                setOtp("");
                formik.resetForm();
                Swal.fire({
                    icon: "error",
                    title: intl.formatMessage({ id: "FORM.LABEL.OTP_FAILED" }),
                    text: intl.formatMessage(
                        { id: "FORM.LABEL.OTP_FAILED_DESC" },
                        {
                            approverName: Array.isArray(currentApprover?.userName)
                                ? currentApprover.userName.join(', ')
                                : currentApprover?.userName || ''
                        }
                    ),
                });
            } finally {
                setProcessingApproval(false);
                setAutoSubmitting(false);
            }
        },
    });

    const handleOtpChange = (newOtp: string) => {
        setOtp(newOtp);
        formik.setFieldValue("otp", newOtp);

        if (newOtp.length === 4 && !processingApproval && !autoSubmitting) {
            setAutoSubmitting(true);
            setTimeout(() => {
                formik.submitForm();
            }, 100);
        }
    };

    const handleApprove = async () => {
        if (!currentApprover || processingApproval) return;

        try {
            setProcessingApproval(true);

            const response = await approveLogAsApprover(leadLogId, pendingId, currentApprover.id, token);

            if (response.data.success) {
                if (currentApproverIndex < approvals.length - 1) {
                    setCurrentApproverIndex(currentApproverIndex + 1);
                    formik.resetForm();
                    setOtp("");
                    resetCountDown();

                    Swal.fire({
                        icon: "success",
                        title: intl.formatMessage(
                            { id: "FORM.LABEL.APPROVAL_SUBMITTED" },
                            { hierarki: currentApprover.hierarchyLevel }
                        ),
                        text: `${intl.formatMessage({ id: "REIMBURSEMENT.STATUS.APPROVED" })} ${Array.isArray(currentApprover.userName)
                            ? `${intl.formatMessage({ id: "FORM.LABEL.ONE_OF_APPROVER" })} ${currentApprover.userName.join(intl.formatMessage({ id: "FORM.LABEL.OR" }))}`
                            : `${intl.formatMessage({ id: "FORM.LABEL.AS" })} ${currentApprover.userName}`
                            }. ${currentApproverIndex < approvals.length - 1 ? (intl.formatMessage({ id: "FORM.LABEL.NEXT_APPROVER" })) : ""}`,
                    }).then(() => {
                        closeModal();
                        navigate(`/pipelines/${templatePipelineId}`);
                    });
                } else {
                    if (tablesData.logs) {
                        const updatedLogs = tablesData.logs.map(log => {
                            if (log.id === leadLogId) {
                                return { ...log, lead_log_status: 'APPROVED' };
                            }
                            return log;
                        });
                        setTablesData('logs', updatedLogs);
                    }

                    if (tablesData.pipelines) {
                        setTablesData('pipelines', tablesData.pipelines.map(item => {
                            if (item.id === leadId) {
                                return {
                                    ...item,
                                    has_pending_log: false,
                                };
                            }
                            return item;
                        }));
                    }

                    Swal.fire({
                        icon: "success",
                        title: intl.formatMessage({ id: "FORM.LABEL.APPROVALS_COMPLETED" }),
                        text: intl.formatMessage({ id: "FORM.LABEL.APPROVALS_COMPLETED_DESC" }),
                    }).then(() => {
                        closeModal();
                        navigate(`/pipelines/${templatePipelineId}`);
                    });
                }
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: intl.formatMessage({ id: "FORM.LABEL.APPROVAL_FAILED" }),
                text: intl.formatMessage(
                    { id: "FORM.LABEL.APPROVAL_FAILED_DESC" },
                    {
                        approverName: Array.isArray(currentApprover?.userName)
                            ? currentApprover.userName.join(', ')
                            : currentApprover?.userName || ''
                    }
                ),
            });
        } finally {
            setProcessingApproval(false);
        }
    };

    const handleReject = async () => {
        if (!currentApprover) return;

        try {
            setProcessingApproval(true);

            const response = await rejectLog(leadLogId, pendingId, currentApprover.id, token);

            if (response.data.success) {
                if (modalInstanceRef.current) {
                    modalInstanceRef.current.hide();
                }

                if (tablesData.logs) {
                    const updatedLogs = tablesData.logs.map(log => {
                        if (log.id === leadLogId) {
                            return { ...log, lead_log_status: 'APPROVED' };
                        }
                        return log;
                    });
                    setTablesData('logs', updatedLogs);
                }
                if (tablesData.pipelines) {
                    setTablesData('pipelines', tablesData.pipelines.map(item => {
                        if (item.id === leadId) {
                            return {
                                ...item,
                                has_pending_log: false,
                            };
                        }
                        return item;
                    }));
                }

                Swal.fire({
                    icon: "info",
                    title: intl.formatMessage({ id: "FORM.LABEL.LOG_REJECTED" }),
                    text: intl.formatMessage(
                        { id: "FORM.LABEL.LOG_REJECTED_DESC" },
                        {
                            approverName: response.data.data.userName[0]
                        }
                    ),
                }).then(() => {
                    closeModal();
                    navigate(`/pipelines/${templatePipelineId}`);
                    window.location.reload();
                });
            }
        } catch (error) {
            console.error("Rejection error:", error);
            Swal.fire({
                icon: "error",
                title: intl.formatMessage({ id: "FORM.LABEL.REJECTION_FAILED" }),
                text: intl.formatMessage(
                    { id: "FORM.LABEL.REJECTION_FAILED_DESC" },
                    {
                        approverName: Array.isArray(currentApprover?.userName)
                            ? currentApprover.userName.join(', ')
                            : currentApprover?.userName || ''
                    }
                ),
            });
        } finally {
            setProcessingApproval(false);
        }
    };

    const handleResend = async () => {
        if (!currentApprover) {
            Swal.fire({
                icon: "error",
                title: intl.formatMessage({ id: "FORM.LABEL.OTP_RESENT_FAILED" }),
                text: intl.formatMessage(
                    { id: "FORM.LABEL.OTP_RESENT_FAILED_DESC" }
                ),
            });
            return;
        }
        try {
            setResendingOtp(true);

            const response = await resendOtp(leadLogId, pendingId, currentApprover.id, token);

            if (response.data.success) {
                Swal.fire({
                    icon: "info",
                    title: intl.formatMessage({ id: "FORM.LABEL.OTP_RESENT" }),
                    text: intl.formatMessage(
                        { id: "FORM.LABEL.OTP_RESENT_DESC" },
                        {
                            approverName: Array.isArray(currentApprover?.userName)
                                ? currentApprover.userName.join(', ')
                                : currentApprover?.userName || ''
                        }
                    ),
                });

                formik.setFieldValue("otp", "");
                setOtp("");
                resetCountDown();
            }
        } catch (error) {
            console.error("OTP resend error:", error);
            Swal.fire({
                icon: "error",
                title: intl.formatMessage({ id: "FORM.LABEL.OTP_RESENT_FAILED" }),
                text: intl.formatMessage(
                    { id: "FORM.LABEL.OTP_RESENT_FAILED_DESC" },
                    {
                        approverName: Array.isArray(currentApprover?.userName)
                            ? currentApprover.userName.join(', ')
                            : currentApprover?.userName || ''
                    }
                ),
            });
        } finally {
            setResendingOtp(false);
        }
    };

    const handleCancel = async (e: any) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }

        const result = await Swal.fire({
            title: intl.formatMessage({ id: 'FORM.VALIDATION.CANCEL' }),
            text: intl.formatMessage({ id: 'FORM.VALIDATION.DESC_CANCEL' }),
            icon: 'warning',
            showCancelButton: true,
            buttonsStyling: false,
            confirmButtonText: intl.formatMessage({ id: 'FORM.CONFIRMATION.YES' }) + ", " + intl.formatMessage({ id: 'FORM.ACTION.CANCEL' }),
            cancelButtonText: intl.formatMessage({ id: 'FORM.CONFIRMATION.NO' }),
            customClass: {
                confirmButton: "btn btn-danger",
                cancelButton: "btn btn-active-light me-3",
            },
            reverseButtons: true,
            heightAuto: false,
        });

        if (result.isConfirmed) {
            try {
                const response = await cancelLog(leadLogId, token);

                if (response.data.success) {
                    if (tablesData.logs) {
                        const updatedLogs = tablesData.logs.map(log => {
                            if (log.id === response.data.data.id) {
                                return {
                                    ...log,
                                    lead_log_status: 'CANCELLED',
                                    lead_progress_name: response.data.data.nowLeadProgressName,
                                    lead_progress_id: response.data.data.nowLeadProgressId,
                                    lead_progress_progress_type: response.data.data.nowLeadProgressType,
                                };
                            }
                            return log;
                        });

                        setTablesData('logs', updatedLogs);
                    }

                    if (tablesData.pipelines) {
                        const hasPendingLog = false;

                        setTablesData('pipelines', tablesData.pipelines.map(item => {
                            if (item.id === leadId) {
                                return {
                                    ...item,
                                    has_pending_log: hasPendingLog,
                                    lead_progress_name: response.data.data.nowLeadProgressName,
                                    lead_progress_id: response.data.data.nowLeadProgressId,
                                    lead_progress_progress_type: response.data.data.nowLeadProgressType,
                                };
                            } else {
                                return item;
                            }
                        }));
                    }

                    Swal.fire({
                        icon: "info",
                        title: intl.formatMessage({ id: 'FORM.LABEL.CANCEL_APPROVALS' }),
                        text: intl.formatMessage({ id: 'FORM.LABEL.CANCEL_APPROVALS_DESC' }),
                    }).then(() => {
                        closeModal();
                        navigate(`/pipelines/${templatePipelineId}`);
                        window.location.reload();
                    });
                }
            } catch (error) {
                console.error("Log Approval cancel error:", error);
                Swal.fire({
                    icon: "error",
                    title: intl.formatMessage({ id: 'FORM.LABEL.CANCEL_APPROVALS_FAILED' }),
                    text: intl.formatMessage({ id: 'FORM.LABEL.CANCEL_APPROVALS_FAILED_DESC' }),
                });
            }
        }
    };

    const fetchApprovalDetails = async () => {
        try {
            setIsLoading(true);

            const leadLogsResponse = await getLeadLogs(leadId, token);

            if (!leadLogsResponse.data.success || !leadLogsResponse.data.data || leadLogsResponse.data.data.length === 0) {
                console.error("No lead logs found in response:", leadLogsResponse);
                throw new Error(intl.formatMessage({ id: 'FORM.LABEL.LEAD_NO_APPROVAL' }));
            }

            const recentLog = leadLogsResponse.data.data[0];
            const logId = recentLog.id;
            setLeadLogId(logId);
            const pendingLogsResponse = await getLogsPending(logId, token);

            if (!pendingLogsResponse.data.success || !pendingLogsResponse.data.data) {
                console.error("No pending logs data found in response:", pendingLogsResponse);
                throw new Error(intl.formatMessage({ id: 'FORM.LABEL.NO_PENDING_APPROVAL' }));
            }

            const pendingData = pendingLogsResponse.data.data;
            if (!pendingData.pendings || pendingData.pendings.length === 0) {
                console.error("No pending approvals in data:", pendingData);
                throw new Error(intl.formatMessage({ id: 'FORM.LABEL.NO_PENDING_APPROVAL' }));
            }

            const pendingIdValue = pendingData.pendings[0].id;
            setPendingId(pendingIdValue);

            const approvalInfoResponse = await getPendingApprovalInfo(logId, pendingIdValue, token);

            if (!approvalInfoResponse.data.success || !approvalInfoResponse.data.data || approvalInfoResponse.data.data.length === 0) {
                console.error("No approval information found in response:", approvalInfoResponse);
                throw new Error(intl.formatMessage({ id: 'FORM.LABEL.NO_APPROVAL' }));
            }

            const sortedApprovals = [...approvalInfoResponse.data.data].sort(
                (a, b) => a.hierarchyLevel - b.hierarchyLevel
            );

            setApprovals(sortedApprovals);
            setCurrentApproverIndex(0);

        } catch (error) {
            let errorMessage = "Error fetching approval information";
            if (error instanceof Error) {
                errorMessage = error.message;
            }

            console.error("Error in fetchApprovalDetails:", error);

            Swal.fire({
                icon: "error",
                title: intl.formatMessage({ id: 'FORM.LABEL.APPROVALS_ERROR' }),
                text: errorMessage,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleInvoiceModalClose = (isSubmit: boolean = false) => {
        setShowInvoiceModal(false)
        closeModal()
    }

    useEffect(() => {
        if (isOpen && modalRef.current) {
            const modalElement = modalRef.current;

            const modalInstance = new Modal(modalElement, {
                backdrop: 'static',
                keyboard: false
            });
            modalInstanceRef.current = modalInstance;

            modalInstance.show();

            if (!currentUser) {
                console.warn("No current user found in store. User authentication may be required.");
                Swal.fire({
                    icon: "warning",
                    title: intl.formatMessage({ id: 'FORM.LABEL.AUTH_ERROR' }),
                    text: intl.formatMessage({ id: 'FORM.LABEL.AUTH_ERROR_DESC' }),
                }).then(() => {
                    closeModal();
                });
            }

            fetchApprovalDetails();

            return () => {
                if (modalInstanceRef.current) {
                    modalInstanceRef.current.dispose();
                    modalInstanceRef.current = null;
                }

                setCurrentApproverIndex(0);
                setApprovals([]);
                setIsCurrentUserApprover(false);
                setOtp("");
                setAutoSubmitting(false);
                formik.resetForm();
            };
        }
    }, [isOpen, leadId, currentUser]);

    return (
        <>
            <div
                className={`modal fade ${settings.feature_invoice && selectInvoice ? 'hide' : 'show'}`}
                style={{ display: settings.feature_invoice && selectInvoice ? "none" : "block" }}
                tabIndex={-1}
                id="otpModal"
                ref={modalRef}
                aria-hidden={!isOpen ? "true" : "false"}
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {isCurrentUserApprover
                                    ? intl.formatMessage({ id: "FORM.LABEL.APPROVAL_LOG" })
                                    : intl.formatMessage({ id: "FORM.LABEL.ENTER_OTP_LOG" })}
                            </h5>
                            <button
                                type="button"
                                className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                                onClick={handleCloseModal}
                                ref={closeModalRef}
                            >
                                <KTSVG path="/media/icons/duotune/arrows/arr061.svg" className="svg-icon svg-icon-2x" />
                            </button>
                        </div>
                        <form onSubmit={formik.handleSubmit} noValidate>
                            <div className="modal-body">
                                {isLoading ? (
                                    <div className="d-flex justify-content-center my-3">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {approvals.length > 0 && !isCurrentUserApprover && currentApprover?.approvedAt === null && (
                                            <div className="mb-4">
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <span className="fs-6 fw-bold text-gray-800">
                                                        {intl.formatMessage({ id: "FORM.LABEL.APPROVER_PROGRESS" })}
                                                    </span>
                                                    <span className="badge bg-primary">
                                                        {currentApproverIndex + 1} of {approvals.length}
                                                    </span>
                                                </div>

                                                <div className="progress mb-4" style={{ height: '8px' }}>
                                                    <div
                                                        className="progress-bar bg-primary"
                                                        role="progressbar"
                                                        style={{
                                                            width: `${((currentApproverIndex) / approvals.length) * 100}%`
                                                        }}
                                                        aria-valuenow={((currentApproverIndex) / approvals.length) * 100}
                                                        aria-valuemin={0}
                                                        aria-valuemax={100}
                                                    ></div>
                                                </div>

                                                {currentApprover && (
                                                    <div className="card bg-light p-4 mb-3">
                                                        <div className="d-flex flex-column">
                                                            <span className="text-gray-700 fs-6 fw-bold mb-1">{intl.formatMessage({ id: "FORM.VALIDATION.CURRENT_APPROVER" })}</span>
                                                            <span className="fs-5 fw-bolder">
                                                                {Array.isArray(currentApprover.userName)
                                                                    ? currentApprover.userName.join(intl.formatMessage({ id: "FORM.LABEL.OR" }))
                                                                    : currentApprover.userName}
                                                            </span>
                                                            <span className="badge bg-info mt-2 align-self-start">
                                                                {intl.formatMessage({ id: "FORM.LABEL.HIERARCHY_LEVEL" })}: {currentApprover.hierarchyLevel}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {isCurrentUserApprover ? (
                                            <div className="text-center">
                                                <h4 className="mb-4">{intl.formatMessage({ id: "FORM.LABEL.SIGN_IN_AS" })} <strong>{currentUser?.name}</strong></h4>
                                                <div className="mb-6 fs-5">
                                                    <div className="text-center">
                                                        <p className="mb-14">{intl.formatMessage({ id: "FORM.LABEL.APPROVAL_ACTION" })}</p>
                                                        <p className="mb-0 fst-italic">{intl.formatMessage({ id: "FORM.LABEL.APPROVAL_ACTION2" })}</p>
                                                    </div>
                                                </div>
                                                <div className="d-flex justify-content-center gap-5">
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger"
                                                        onClick={handleReject}
                                                        disabled={processingApproval}
                                                    >
                                                        <KTSVG
                                                            path="/media/icons/duotune/general/gen040.svg"
                                                            className="svg-icon svg-icon-2 me-2"
                                                        />
                                                        {intl.formatMessage({ id: "FORM.ACTION.REJECT" })}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-success"
                                                        onClick={handleApprove}
                                                        disabled={processingApproval}
                                                    >
                                                        <KTSVG
                                                            path="/media/icons/duotune/general/gen043.svg"
                                                            className="svg-icon svg-icon-2 me-2"
                                                        />
                                                        {intl.formatMessage({ id: "FORM.ACTION.APPROVE" })}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="mt-4">
                                                <div className="text-center">
                                                    <p className="fs-5 p-5 pb-0">
                                                        <strong>{intl.formatMessage({ id: "FORM.LABEL.ENTER_VERIFICATION_OTP" })}</strong>
                                                    </p>
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-center flex-wrap">
                                                            {Array.isArray(currentApprover?.userPhoneNumber) ? (
                                                                currentApprover?.userPhoneNumber.map((phone, index) => (
                                                                    <React.Fragment key={index}>
                                                                        <label className="form-label fw-bolder text-dark fs-3 mb-0" style={{ marginBottom: "4px" }}>
                                                                            {"**********"}
                                                                        </label>
                                                                        <label className="form-label fw-bolder text-dark fs-3 mb-0" style={{ marginRight: "8px" }}>
                                                                            {phone?.toString().slice(-3)} {''}
                                                                        </label>
                                                                        {index !== currentApprover?.userPhoneNumber.length - 1 && (
                                                                            <label className="form-label fw-bolder text-dark fs-3 mb-0" style={{ marginRight: "8px" }}>
                                                                                {intl.formatMessage({ id: "FORM.LABEL.OR" })}
                                                                            </label>
                                                                        )}
                                                                    </React.Fragment>
                                                                ))
                                                            ) : (
                                                                <>
                                                                    <label className="form-label fw-bolder text-dark fs-3 mb-0">
                                                                        {"**********"}
                                                                    </label>
                                                                    <label className="form-label fw-bolder text-dark fs-3 mb-0">
                                                                        {currentApprover?.userPhoneNumber?.toString().slice(-3)}
                                                                    </label>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <OTPInput
                                                    value={otp}
                                                    onChange={handleOtpChange}
                                                    numInputs={4}
                                                    renderSeparator={<span></span>}
                                                    containerStyle={{
                                                        justifyContent: 'center',
                                                        gap: "10px"
                                                    }}
                                                    renderInput={(props) =>
                                                        <input
                                                            {...props}
                                                            maxLength={1}
                                                            className="form-control form-control-solid h-60px mw-60px fs-2hx text-center border-active-primary mx-1 my-2"
                                                            style={{ width: '16%' }}
                                                            disabled={processingApproval}
                                                        />}
                                                    inputStyle={{
                                                        width: "60px",
                                                        height: "60px",
                                                        fontSize: "24px",
                                                        borderRadius: "8px",
                                                        border: "1px solid #E4E6EF",
                                                        textAlign: "center"
                                                    }}
                                                />
                                                {formik.touched.otp && formik.errors.otp && (
                                                    <div className="invalid-feedback">{formik.errors.otp}</div>
                                                )}
                                                <div className="text-center fw-bold fs-5 mt-5">
                                                    <span className="text-muted me-1">{intl.formatMessage({ id: "FORM.LABEL.NOT_RECEIVE_CODE" })}</span>
                                                    <span
                                                        className="link-primary fw-bolder fs-5 me-1"
                                                        style={{ cursor: countDown > 0 || resendingOtp ? 'not-allowed' : 'pointer' }}
                                                        id={`resendOTP`}
                                                        role="button"
                                                        tabIndex={countDown ? -1 : 0}
                                                        onClick={countDown > 0 || resendingOtp ? undefined : handleResend}
                                                        data-kt-indicator={isLoading ? 'on' : 'off'}
                                                    >
                                                        <span className="indicator-label">
                                                            {countDown === 0
                                                                ? intl.formatMessage({ id: "FORM.ACTION.RESEND" })
                                                                : `${formatCountdown(countDown)}`}
                                                        </span>
                                                        <span className="indicator-progress">
                                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                                        </span>
                                                    </span>
                                                </div>
                                                <div className="text-center fw-bold fs-5 mt-5">
                                                    <span className="text-muted me-1">{intl.formatMessage({ id: "FORM.LABEL.CANCEL_APPROVAL" })}</span>
                                                    <span
                                                        className="link-primary fw-bolder fs-5 me-1"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={handleCancel}
                                                    >
                                                        {intl.formatMessage({ id: "FORM.VALIDATION.CANCEL" })} {intl.formatMessage({ id: "FORM.LABEL.APPROVAL" })}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            {!isCurrentUserApprover && (
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className='btn btn-light'
                                        ref={closeModalRef}
                                        onClick={handleCloseModal}
                                    >
                                        {intl.formatMessage({ id: 'FORM.ACTION.CLOSE' })}
                                    </button>
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </div>

            {settings.feature_invoice && selectInvoice && (
                <InvoiceAttachments
                    lead={tablesData.pipelines?.find(item => item.id === leadId)}
                    invoice={selectInvoice}
                    context={invoiceContext}
                    routerContext='log'
                    showInvoiceModal={showInvoiceModal}
                    handleInvoiceModalClose={handleInvoiceModalClose}
                />
            )}
        </>
    );
};

export default OTPModal;